import { FORM_FIELD_CONSTRAINTS } from './form-constraints';

export type ClientFormState = Record<string, unknown>;
type PaymentProvider = 'Lydia' | 'SumUp' | 'Stripe' | 'PayPal' | 'Autre';

const TEXT_LIMITS: Record<string, number> = {
  territoryId: FORM_FIELD_CONSTRAINTS.territoryId.maxLength,
  companyName: FORM_FIELD_CONSTRAINTS.companyName.maxLength,
  siret: FORM_FIELD_CONSTRAINTS.siret.maxLength,
  vatNumber: FORM_FIELD_CONSTRAINTS.vatNumber.maxLength,
  address: FORM_FIELD_CONSTRAINTS.address.maxLength,
  displayName: FORM_FIELD_CONSTRAINTS.displayName.maxLength,
  representativeName: FORM_FIELD_CONSTRAINTS.representativeName.maxLength,
  email: FORM_FIELD_CONSTRAINTS.email.maxLength,
  phone: FORM_FIELD_CONSTRAINTS.phone.maxLength,
  professionalPhone: FORM_FIELD_CONSTRAINTS.professionalPhone.maxLength,
  joeDriverPhone: FORM_FIELD_CONSTRAINTS.joeDriverPhone.maxLength,
  circleDrivers: FORM_FIELD_CONSTRAINTS.circleDrivers.maxLength,
  partnerPaymentUrl: FORM_FIELD_CONSTRAINTS.partnerPaymentUrl.maxLength,
};
const LEGAL_FORMS = new Set([
  'Entreprise individuelle',
  'Micro-entreprise',
  'EURL',
  'SARL',
  'SASU',
  'SAS',
  'SA',
  'Association',
  'Autre',
]);
const PAYMENT_PROVIDERS = new Set<PaymentProvider>(['Lydia', 'SumUp', 'Stripe', 'PayPal', 'Autre']);
const DECIMAL_PLACES = FORM_FIELD_CONSTRAINTS.pricePerKmTtc.maxDecimals;
const DECIMAL_PATTERN = new RegExp(`^\\d+(?:\\.\\d{1,${DECIMAL_PLACES}})?$`, 'u');
const LOCAL_OR_RESERVED_SUFFIXES = ['.localhost', '.local', '.internal', '.lan', '.home', '.test', '.invalid'] as const;
const SHORTENER_DOMAINS = new Set([
  'bit.ly', 'tinyurl.com', 't.co', 'rebrand.ly', 'cutt.ly', 'is.gd', 'tiny.one', 'rb.gy', 'shorturl.at',
]);
const STRIPE_PAYMENT_HOSTS = new Set(['buy.stripe.com', 'book.stripe.com', 'donate.stripe.com']);

function text(state: ClientFormState, key: string): string {
  return String(state[key] ?? '').trim();
}

function required(state: ClientFormState, key: string, message: string): string | null {
  return text(state, key) ? null : message;
}

function maxLength(value: string, key: keyof typeof TEXT_LIMITS, label: string): string | null {
  return Array.from(value).length <= TEXT_LIMITS[key]!
    ? null
    : `${label} est trop long.`;
}

function normalizeFrenchPhone(value: string): string {
  const compact = value.trim().replace(/[\s.-]+/gu, '');
  if (/^\+33[1-9]\d{8}$/.test(compact)) return `0${compact.slice(3)}`;
  return compact;
}

function isValidFrenchPhone(value: string): boolean {
  return /^0[1-9]\d{8}$/.test(normalizeFrenchPhone(value));
}

function luhnValid(value: string): boolean {
  let sum = 0;
  const parity = value.length % 2;
  for (let index = 0; index < value.length; index += 1) {
    let digit = Number(value[index]);
    if (!Number.isInteger(digit)) return false;
    if (index % 2 === parity) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

function normalizeSiret(value: string): string {
  return value.trim().replace(/\s+/gu, '');
}

function isValidSiret(value: string): boolean {
  const normalized = normalizeSiret(value);
  return /^\d{14}$/.test(normalized) && luhnValid(normalized) && luhnValid(normalized.slice(0, 9));
}

function isValidFrenchVatNumber(value: string, siret: string): boolean {
  const normalized = value.trim().replace(/\s+/gu, '').toUpperCase();
  const normalizedSiret = normalizeSiret(siret);
  return /^FR[A-Z0-9]{2}\d{9}$/.test(normalized) &&
    /^\d{14}$/.test(normalizedSiret) &&
    normalized.slice(4) === normalizedSiret.slice(0, 9);
}

function isValidEmail(value: string): boolean {
  const normalized = value.trim();
  if (!normalized || Array.from(normalized).length > 254 || /[\u0000-\u001F\u007F]/u.test(normalized)) return false;
  const at = normalized.lastIndexOf('@');
  if (at <= 0 || at !== normalized.indexOf('@')) return false;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (Array.from(local).length > 64 || !domain.includes('.') || domain.length > 253) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..') || !/^[^\s@]+$/u.test(local)) return false;
  return domain.split('.').every((label) =>
    label.length >= 1 &&
    label.length <= 63 &&
    /^[A-Za-z0-9-]+$/.test(label) &&
    !label.startsWith('-') &&
    !label.endsWith('-'),
  );
}

function normalizedHost(url: URL): string {
  return url.hostname.toLowerCase().replace(/\.$/u, '');
}

function isIpLiteral(host: string): boolean {
  const value = host.replace(/^\[/u, '').replace(/\]$/u, '');
  if (value.includes(':')) return true;
  return /^\d{1,3}(?:\.\d{1,3}){3}$/u.test(value);
}

function isPublicPaymentHost(host: string): boolean {
  if (!host || host === 'localhost' || !host.includes('.') || isIpLiteral(host)) return false;
  return !LOCAL_OR_RESERVED_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

function knownProvider(host: string): Exclude<PaymentProvider, 'Autre'> | null {
  if (host === 'money.lydia.me' || host === 'lydia-app.com') return 'Lydia';
  if (host === 'pay.sumup.com') return 'SumUp';
  if (host === 'paypal.me' || host === 'paypal.com' || host === 'www.paypal.com') return 'PayPal';
  if (STRIPE_PAYMENT_HOSTS.has(host)) return 'Stripe';
  return null;
}

function hasUsablePath(url: URL): boolean {
  return url.pathname.split('/').some((segment) => segment.trim().length > 0);
}

function normalizePaymentUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || /^[a-z][a-z\d+.-]*:/iu.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

function paymentUrlError(provider: PaymentProvider, value: string): string | null {
  const normalized = normalizePaymentUrl(value);
  if (!normalized || Array.from(normalized).length > FORM_FIELD_CONSTRAINTS.partnerPaymentUrl.maxLength || /[\u0000-\u001F\u007F]/u.test(normalized)) {
    return 'Le lien de paiement doit être une URL valide, avec ou sans protocole, sans identifiants intégrés.';
  }

  let url: URL;
  try {
    url = new URL(normalized);
  } catch {
    return 'Le lien de paiement doit être une URL valide, avec ou sans protocole, sans identifiants intégrés.';
  }
  if ((url.protocol.toLowerCase() !== 'http:' && url.protocol.toLowerCase() !== 'https:') || !url.hostname || url.username || url.password) {
    return 'Le lien de paiement doit être une URL valide, avec ou sans protocole, sans identifiants intégrés.';
  }

  const host = normalizedHost(url);
  if (!isPublicPaymentHost(host)) return 'Le lien de paiement doit pointer vers un domaine public.';
  if (url.port) return 'Le lien de paiement ne doit pas utiliser de port réseau personnalisé.';
  if ([...SHORTENER_DOMAINS].some((domain) => hostMatches(host, domain))) {
    return 'Les raccourcisseurs d’URL ne sont pas autorisés pour un lien de paiement.';
  }

  if (provider === 'Lydia') {
    const moneyLydia = host === 'money.lydia.me' && hasUsablePath(url);
    const lydiaPro = host === 'lydia-app.com' &&
      url.pathname === '/form/payment/phoneform' &&
      Boolean(url.searchParams.get('vendor_token'));
    return moneyLydia || lydiaPro
      ? null
      : 'Pour Lydia, utilisez money.lydia.me/… ou lydia-app.com/form/payment/phoneform?vendor_token=…';
  }
  if (provider === 'SumUp') {
    return host === 'pay.sumup.com' && hasUsablePath(url)
      ? null
      : 'Pour SumUp, utilisez un lien de paiement hébergé sur pay.sumup.com/…';
  }
  if (provider === 'PayPal') {
    const paypalMe = host === 'paypal.me' && hasUsablePath(url);
    const business = (host === 'paypal.com' || host === 'www.paypal.com') &&
      /^\/ncp\/payment\/[A-Za-z0-9_-]{6,}(?:\/)?$/u.test(url.pathname);
    return paypalMe || business
      ? null
      : 'Pour PayPal, utilisez un lien PayPal.Me ou un lien Business paypal.com/ncp/payment/…';
  }
  if (provider === 'Stripe') {
    const detected = knownProvider(host);
    if (detected && detected !== 'Stripe') return `Le domaine du lien correspond à ${detected}, pas à Stripe.`;
    return hasUsablePath(url) ? null : 'Le lien Stripe est incomplet.';
  }

  const detected = knownProvider(host);
  if (detected) return `Ce lien appartient à ${detected}. Sélectionnez ${detected} comme prestataire.`;
  if (host.split('.').some((label) => label.startsWith('xn--'))) {
    return 'Les domaines internationalisés ne sont pas autorisés avec le prestataire « Autre ».';
  }
  return null;
}

function decimalError(
  state: ClientFormState,
  key: string,
  label: string,
  max: number,
  optional = false,
): string | null {
  const raw = text(state, key);
  if (!raw) return optional ? null : `${label} est obligatoire.`;
  if (!DECIMAL_PATTERN.test(raw)) return `${label} doit contenir au maximum 2 décimales.`;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > max) {
    return `${label} doit être compris entre 0 et ${max.toLocaleString('fr-FR')}.`;
  }
  return null;
}

function circleDriversError(value: string): string | null {
  if (!value) return null;
  if (Array.from(value).length > TEXT_LIMITS.circleDrivers!) return 'Le Cercle de chauffeurs est trop long.';
  const drivers = value.split(/\r?\n/u).map((item) => item.trim()).filter(Boolean);
  if (drivers.length > 50) return 'Le Cercle est limité à 50 chauffeurs.';

  const normalized = new Set<string>();
  for (const driver of drivers) {
    if (!isValidFrenchPhone(driver)) return `Numéro de chauffeur invalide : ${driver}`;
    const phone = normalizeFrenchPhone(driver);
    if (normalized.has(phone)) return `Numéro de chauffeur en doublon : ${phone}`;
    normalized.add(phone);
  }
  return null;
}

export function clientFieldError(key: string, state: ClientFormState): string | null {
  const value = text(state, key);
  switch (key) {
    case 'territoryId': {
      const missing = required(state, key, 'Sélectionnez un territoire.');
      return missing || maxLength(value, 'territoryId', 'Le territoire');
    }
    case 'companyName': {
      const missing = required(state, key, 'La raison sociale est obligatoire.');
      return missing || maxLength(value, 'companyName', 'La raison sociale');
    }
    case 'legalForm':
      if (!value) return 'La forme juridique est obligatoire.';
      return LEGAL_FORMS.has(value) ? null : 'La forme juridique sélectionnée est invalide.';
    case 'siret':
      if (!value) return 'Le SIRET est obligatoire.';
      if (Array.from(value).length > TEXT_LIMITS.siret!) return 'Le SIRET est trop long.';
      if (Array.from(normalizeSiret(value)).length !== FORM_FIELD_CONSTRAINTS.siret.normalizedLength || !/^\d{14}$/u.test(normalizeSiret(value))) {
        return 'Le SIRET doit contenir exactement 14 chiffres.';
      }
      return isValidSiret(value) ? null : 'Le SIRET est invalide.';
    case 'vatNumber':
      if (!value) return null;
      if (Array.from(value).length > TEXT_LIMITS.vatNumber!) return 'Le numéro de TVA est trop long.';
      return isValidFrenchVatNumber(value, text(state, 'siret'))
        ? null
        : 'Le numéro de TVA est invalide ou incohérent avec le SIRET.';
    case 'address': {
      const missing = required(state, key, 'L’adresse complète est obligatoire.');
      return missing || maxLength(value, 'address', 'L’adresse');
    }
    case 'displayName': {
      const missing = required(state, key, 'Le nom affiché / enseigne est obligatoire.');
      return missing || maxLength(value, 'displayName', 'Le nom affiché / enseigne');
    }
    case 'representativeName': {
      const missing = required(state, key, 'Le nom et prénom du représentant sont obligatoires.');
      return missing || maxLength(value, 'representativeName', 'Le représentant');
    }
    case 'phone':
      if (!value) return 'Le téléphone est obligatoire.';
      return maxLength(value, 'phone', 'Le téléphone') || (isValidFrenchPhone(value) ? null : 'Saisissez un numéro français valide.');
    case 'email':
      if (!value) return 'L’adresse e-mail est obligatoire.';
      return isValidEmail(value) ? null : 'Saisissez une adresse e-mail valide.';
    case 'professionalPhone':
      if (!value) return 'Le numéro professionnel est obligatoire.';
      return maxLength(value, 'professionalPhone', 'Le numéro professionnel') || (isValidFrenchPhone(value) ? null : 'Saisissez un numéro professionnel français valide.');
    case 'priorityEnabled':
      return typeof state.priorityEnabled === 'boolean' ? null : 'Choisissez Oui ou Non.';
    case 'joeDriverPhone':
      if (state.priorityEnabled !== true) return null;
      if (!value) return 'Le téléphone Joe Driver est obligatoire lorsque la priorité est activée.';
      return maxLength(value, 'joeDriverPhone', 'Le téléphone Joe Driver') || (isValidFrenchPhone(value) ? null : 'Saisissez un numéro Joe Driver français valide.');
    case 'circleDrivers':
      return circleDriversError(value);
    case 'networkExpansionAllowed':
      return typeof state.networkExpansionAllowed === 'boolean' ? null : 'Choisissez Oui ou Non.';
    case 'pricePerKmTtc':
      return decimalError(state, key, 'Le prix kilométrique TTC', 100);
    case 'minimumPriceTtc':
      return decimalError(state, key, 'Le prix minimum TTC', 10_000);
    case 'partnerCommissionPercent':
      return decimalError(state, key, 'La commission Partenaire', 100);
    case 'minimumCommissionTtc': {
      const base = decimalError(state, key, 'Le minimum de commission TTC', 10_000, true);
      if (base) return base;
      if (!value) return null;
      const minimumCommission = Number(value);
      const minimumPrice = Number(text(state, 'minimumPriceTtc'));
      const commission = Number(text(state, 'partnerCommissionPercent'));
      if (Number.isFinite(minimumPrice) && minimumCommission > minimumPrice) {
        return 'Le minimum de commission ne peut pas dépasser le prix minimum TTC.';
      }
      if (commission === 0 && minimumCommission > 0) {
        return 'Un minimum de commission positif est incompatible avec une commission à 0 %.';
      }
      return null;
    }
    case 'paymentProvider':
      if (!value) return 'Sélectionnez un prestataire de paiement.';
      return PAYMENT_PROVIDERS.has(value as PaymentProvider)
        ? null
        : 'Le prestataire de paiement sélectionné est invalide.';
    case 'partnerPaymentUrl': {
      if (!value) return 'Le lien de paiement est obligatoire.';
      const provider = text(state, 'paymentProvider') as PaymentProvider;
      if (!PAYMENT_PROVIDERS.has(provider)) return null;
      return paymentUrlError(provider, value);
    }
    case 'certification':
      return state.certification === true ? null : 'Cette certification est obligatoire avant l’envoi.';
    default:
      return null;
  }
}

export function clientStepKeys(step: number, state: ClientFormState): string[] {
  if (step === 0) return ['territoryId'];
  if (step === 1) {
    return [
      'companyName', 'legalForm', 'siret', 'vatNumber', 'address', 'displayName',
      'representativeName', 'phone', 'email', 'professionalPhone',
    ];
  }
  if (step === 2) {
    return [
      'priorityEnabled',
      ...(state.priorityEnabled === true ? ['joeDriverPhone'] : []),
      'circleDrivers',
      'networkExpansionAllowed',
    ];
  }
  if (step === 3) {
    return ['pricePerKmTtc', 'minimumPriceTtc', 'partnerCommissionPercent', 'minimumCommissionTtc'];
  }
  if (step === 4) return ['paymentProvider', 'partnerPaymentUrl'];
  if (step === 5) return ['certification'];
  return [];
}

export function validateClientStep(step: number, state: ClientFormState): string[] {
  return clientStepKeys(step, state)
    .map((key) => clientFieldError(key, state))
    .filter((message): message is string => Boolean(message));
}
